/**
 * Campaign System Types
 * Defines the structure for reusable campaign components
 */

import type { IParsedTournament, ITournament, ITournamentResources, TournamentStatuses } from "./variants/tournament.types";
import type {
 IPrizePopCampaign,
 IPrizePopResources,
 PrizePopStatuses,
 IPrizePopPrizesResponse,
 PrizePopWinsResponse,
 PrizePopFormula
} from './variants/prizePop.types';
import type { 
  IRebateCampaign,
  IRebateResources,
  RebateStatuses
} from "./variants/rebate.types";
import type { CampaignTypes } from "./campaignShared.types";
import type { ISectionProps } from "types/models/ISectionProps";


export * from './variants/prizePop.types';
export {
  TOURNAMENTS_CAMPAIGN_TYPE,
  // fallow-ignore-next-line unused-export
  TOURNAMENTS_DISPLAY_NAME,
  type ITournamentPrize,
  type ITournamentScoreBoard,
  IGameModes,
  type IEligibleGameModes,
  type IPlayerScore,
  TournamentStatuses,
  TournamentFormulas,
  type ITournamentResources,
  type ITournamentCampaignInfo,
  type ITournament,
  type IParsedTournament,
  type IFormulaParams,
  type ITournamentSessionData,
  type ILeaderboard,
  type IJoinTournamentSessionData,
  type IJoinTournamentProps,
  type IJoinTournamentResponse,
  type IJoinError,
  type IGetLeaderboardDataProps,
  type ITournamentAPIResponse,
  type PrizeGiftSpinConfig,
  type TournamentPrizeType,
} from './variants/tournament.types';
export * from './variants/rebate.types';
export * from './campaignShared.types';

export type CampaignStatus = PrizePopStatuses | TournamentStatuses | RebateStatuses;
export type CampaignFormula = PrizePopFormula;
export type ResourceType = IPrizePopResources | ITournamentResources | IRebateResources;
export type CampaignSource = IPrizePopCampaign | ITournament | IParsedTournament | IRebateCampaign;
export type CampaignPatch = Partial<IPrizePopCampaign> | Partial<ITournament> | Partial<IParsedTournament> | Partial<IRebateCampaign>;

type CampaignTabType = 'games' | 'prizes' | 'players' | 'rules' | 'formula' | 'rewards' | 'ranking' | 'winnings';


export interface ICampaignSingleViewSectionProps {
  sectionName: string;
}

export interface ICampaignMultiViewSectionProps {
  data: ISectionProps['data'];
  isActive: boolean;
}

export interface CampaignTab {
  type: CampaignTabType;
  label: string;
  component?: React.ComponentType;
}


/**
 * Simplified map interface for campaign card components only
 */
export interface ICampaignCardMap {
  CAMPAIGN_TOURNAMENTS_CARD_INFO: React.ComponentType<Omit<ICampaignInfo, 'component'>>;
  CAMPAIGN_PRIZE_POP_CARD_INFO: React.ComponentType<Omit<ICampaignInfo, 'component'>>;
  CAMPAIGN_REBATE_CARD_INFO: React.ComponentType<Omit<ICampaignInfo, 'component'>>;
}

export interface IComponentCampaignTabsMap {
  // Tournament tabs
  TOURNAMENTS_GAMES_TAB: React.ComponentType;
  TOURNAMENTS_REWARDS_TAB: React.ComponentType;
  TOURNAMENTS_RANKING_TAB: React.ComponentType;
  TOURNAMENTS_RULES_TAB: React.ComponentType;

  // Campaign tabs
  PRIZE_POP_GAMES_TAB: React.ComponentType;
  PRIZE_POP_PRIZES_TAB: React.ComponentType;
  PRIZE_POP_PLAYERS_TAB: React.ComponentType;
  PRIZE_POP_RULES_TAB: React.ComponentType;

  // Rebate tabs — optional: the components are not implemented yet
  REBATE_GAMES_TAB?: React.ComponentType;
  REBATE_WINNINGS_TAB?: React.ComponentType;
  REBATE_RULES_TAB?: React.ComponentType;

  // Allow additional keys for extensibility
  [key: string]: React.ComponentType | undefined;
}

export interface ICampaignInfo {
  id: string;
  name: string;
  startDate?: string;
  endDate?: string;
  resources: ResourceType;
  baseCurrency: string;
  totalPrizes?: number;
  component: React.ComponentType<Omit<ICampaignInfo, 'component'>>;
  campaignId?: string;
  joined?: boolean;
  status: CampaignStatus;
  type?: CampaignTypes;
}

export type CampaignInfoViewType = 'panel' | 'card';

export const CAMPAIGN_INFO_TYPES = {
  PERIOD: 'period',
  TIMER: 'timer',
  PRIZES: 'prizes',
  POSITION: 'position',
  SCORE: 'score',
  NEXT_WIN: 'nextWin',
  PROGRESS: 'progress',
} as const;

export type CampaignInfoType = typeof CAMPAIGN_INFO_TYPES[keyof typeof CAMPAIGN_INFO_TYPES];

export interface CampaignInfoItem {
  iconSrc: string;
  type: CampaignInfoType;
  label: string;
  value: string;
  suffix?: string;
}


export interface CampaignsResponse {
  campaigns: CampaignSource[];
  autoJoin: boolean;
  playerName: string;
  totalPrizesCount: number;
}

/**
 * Union type for campaign prizes responses
 * Different campaign types may have different prize response structures
 */
export type ICampaignPrizesResponse = IPrizePopPrizesResponse;

/**
 * Union type for campaign wins responses
 * Different campaign types may have different wins response structures
 */
export type ICampaignWinsResponse = PrizePopWinsResponse;

